import React from 'react'
import get from 'lodash/fp/get'
import flow from 'lodash/fp/flow'
import toInteger from 'lodash/fp/toInteger'
import Button from 'react-bootstrap/Button'
import { useQuery } from '@apollo/react-hooks'

import { Booking } from '../../../types'
import { Loader } from '../../../components'
import { GET_GUEST_GROUP } from '../../../graphql'
import { Common404 } from '../../../components/Error404'
import { offeringTypes } from '../../../common/constants'
import { useAppSelector, useAppDispatch } from '../../../hooks'
import { setHidePaymentCTA } from '../../../features/bookingSlice'

import {
  PackageDefaultCard,
  getOfferingBasicField,
  guestInfoDisplay,
} from './bookingFunctions'

import { FreeGuests, PaidGuests, ExtraNights } from '.'

export const BookingAddons: React.FC<{
  booking: Booking
}> = ({ booking }) => {
  const { data: groupData, loading: loadingGroup } = useQuery(GET_GUEST_GROUP, {
    variables: { eventId: booking.eventComponent.eventId },
    fetchPolicy: 'cache-and-network',
  })
  const dispatch = useAppDispatch()
  const { eventComponent } = booking
  const { offering, customTexts } = eventComponent
  const {
    hidePaymentCTA,
    availableAddons: { extraGuestAddon, extraKidAddon, extraFreeGuestAddon },
  } = useAppSelector(state => state.booking)

  if (loadingGroup) {
    return (
      <Loader
        className=" pt-5 text-center"
        text="Loading group information..."
      />
    )
  }
  if (!groupData && !loadingGroup) {
    if (!hidePaymentCTA) {
      dispatch(setHidePaymentCTA(true))
    }
    return (
      <Common404 text="Group data not found">
        <Button
          variant="outline-primary"
          className="d-block mx-auto mt-3"
          onClick={() => window.history.back()}
        >
          Go Back
        </Button>
      </Common404>
    )
  }

  const isRoom = offering.__typename === offeringTypes.roomOffering
  const basic = getOfferingBasicField(offering)

  const hasGuestAddons = extraGuestAddon || extraKidAddon || extraFreeGuestAddon
  const hasPaidGuestAddons = extraGuestAddon || extraKidAddon

  if (!hasGuestAddons && !hasPaidGuestAddons && !isRoom) {
    // To not render an empty card
    return <PackageDefaultCard component={eventComponent} />
  }

  const indication =
    customTexts?.guests_indication ??
    'Enter legal name of all guests in this room as it appears on their passport, then save.'

  /*
   * For the moment, we only allow the guest to edit the free guests
   * if we allow the guest to edit the paid guests, we need to update the api as well in order to generate an extra payment
   * calculate new total price and update the booking
   * in the other hand, if the guest remove paid stuff, we need to refund and we can't do that yet
   */
  const occupancy = flow(get('room.occupancy'), toInteger)(offering)
  const hidePaidGuestAddons = isRoom ? occupancy - basic <= 0 : false // if its a package we dont have that problem because the capacity is big enough

  return (
    <div className="w-100 my-4 py-4 px-3 card">
      {isRoom && (
        <>
          <h4 className="text-left mb-3">
            Choose your stay{' '}
            <span className="fz-12 mb-3 tc-lgray">
              you can add extra nights before and after the event
            </span>
          </h4>
          <ExtraNights booking={booking} />
          <hr className="my-5" />
        </>
      )}
      {hasGuestAddons && (
        <>
          <h4 className="text-left mb-2">
            Guests Information {guestInfoDisplay(basic, isRoom)}
          </h4>
          <p className="fz-16">{indication}</p>
        </>
      )}

      {extraFreeGuestAddon && (
        <FreeGuests
          basic={basic}
          guestShort={booking.guest}
          showPlusOne={isRoom}
          myGroup={groupData?.me?.myGroup || []}
        />
      )}
      {hasPaidGuestAddons && !hidePaidGuestAddons && (
        <>
          <hr className="my-5" />
          <h4 className="text-left mb-1">
            {customTexts?.paid_guests_title ?? 'Extra guests'}
          </h4>
          <PaidGuests
            booking={booking}
            showPlusOne={isRoom}
            myGroup={groupData?.me?.myGroup || []}
          />
        </>
      )}
    </div>
  )
}
